import { IconButton } from "@material-ui/core";
import {
  Close as CloseIcon,
  CompareArrows as CompareArrowsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from "@material-ui/icons";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ContextualSaveBar,
  Modal,
  Page,
  ResourceItem,
  ResourceList,
  TextContainer,
  TextField,
  TextStyle,
  Thumbnail,
} from "@shopify/polaris";
import { useAdmin } from "context/admin";
import { useUser } from "context/user";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import LoadingPage from "shared/LoadingPage";
import styled from "styled-components";
import { postData } from "util/fetch";
import { orderShippingMethod, orderTitle } from "util/helpers";
import PackingSlipModal from "views/Modals/PackingSlipModal";
import ParcelLabelModal from "views/Modals/ParcelLabelModal";
import ShippingLabelsModal from "views/Modals/ShippingLabelsModal";
import PrintOrders from "./PrintOrders";
import SearchProductsModal from "./SearchProductsModal";
import ChangeFulfillmentLocationModal from "./components/ChangeFulfillmentLocationModal";

const ListItem = styled.div`
  display: flex;

  @media screen and (min-width: 600px) {
    align-items: center;
    justify-content: space-between;
  }
  @media screen and (max-width: 600px) {
    flex-direction: column;
  }
`;
const changeTypes = {
  updatePrice: "UPDATE_PRICE",
  updateQuantity: "UPDATE_QUANTITY",
  substituteCustom: "SUBSTITUTE_CUSTOM",
  substituteVariant: "SUBSTITUTE_VARIANT",
};

// const changes = {
//   [line_item_id]: { type: 'UPDATE_PRICE', payload: { actual_price: '', original_price: '' } },
//   [line_item_id]: { type: 'SUBSTITUTE_CUSTOM', payload: { id: '', title: '', price: '' } },
//   [line_item_id]: { type: 'SUBSTITUTE_VARIANT', payload: { id: '', quantity: '' } }
// }

export default function OrderPage() {
  const { orderId } = useParams();
  const {
    orders,
    deliveries,
    selectedShop,
    navigateToShopify,
    navigateExternal,
    selectedShopId,
    updateOrder,
    setToast,
    loadingProducts,
    changeFulfillmentLocation,
    markOrderPickedUp,
    markOrderDelivered,
    setOrderTotal,
    fetchBlobFromLink,
  } = useAdmin();

  const order = orders.entities[orderId];
  const delivery = deliveries.entities[orderId];

  const computeDisplayTotal = () => {
    if (!order) return "";
    
    const toNumber = (val) => {
      const n = parseFloat(val);
      return Number.isFinite(n) ? n : 0;
    };

    // Use the effective total calculated by the server (accounts for refunds)
    if (order.effective_total !== undefined) {
      const serverResult = toNumber(order.effective_total).toFixed(2);
      console.log(`[OrderPage] computeDisplayTotal order=${order.name} server_effective_total=${serverResult}`, {
        effective_total: order.effective_total,
        current_total_price: order.current_total_price,
        total_price: order.total_price,
        total_refunded: order.total_refunded,
        total_outstanding: order.total_outstanding,
        financial_status: order.financial_status
      });
      return serverResult;
    }

    // Fallback logic
    const baseTotal = toNumber(order.current_total_price || order.total_price);
    const totalRefunded = toNumber(order.total_refunded);
    const fallbackResult = (baseTotal - totalRefunded).toFixed(2);
    
    console.log(`[OrderPage] computeDisplayTotal order=${order.name} fallback_total=${fallbackResult}`, {
      baseTotal,
      totalRefunded,
      current_total_price: order.current_total_price,
      total_price: order.total_price
    });

    return fallbackResult;
  };

  const displayTotalValue = computeDisplayTotal();

  const user = useUser();
  const userCanView = true; // user && (user.isAdmin || user.betaAccess || user.isVendor);
  const userCanEdit = false; // userCanView && !user.isVendor;

  const [selectedShippingLabel, setSelectedShippingLabel] = useState();
  const [showPackingSlipModal, setShowPackingSlipModal] = useState(false);
  const [parcelLabelOpen, setParcelLabelOpen] = useState(false);
  const [parcelLabelLoading, setParcelLabelLoading] = useState(false);
  const [parcelLabelUrl, setParcelLabelUrl] = useState(null);
  const [parcelLabelFilename, setParcelLabelFilename] = useState(null);
  const history = useHistory();

  const [changes, setChanges] = useState({});
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(false);
  const [openSearchModal, setOpenSearchModal] = useState(false);
  const [changeLocationModalOpen, setChangeLocationModalOpen] = useState(false);
  const [changingLocation, setChangingLocation] = useState(false);
  const [markingPickedUp, setMarkingPickedUp] = useState(false);
  const [markingDelivered, setMarkingDelivered] = useState(false);
  const [targetTotal, setTargetTotal] = useState(displayTotalValue || "");
  const [settingTotal, setSettingTotal] = useState(false);
  const [showSetTotalModal, setShowSetTotalModal] = useState(false);
  const [totalNote, setTotalNote] = useState("");
  const [confirmTotalChange, setConfirmTotalChange] = useState(false);

  useEffect(() => {
    if (order && (!changes || !editing)) {
      setChanges(
        order.line_items.reduce(
          (acc, item) => ({ ...acc, [item.id]: item.price }),
          {}
        )
      );
      setEditing(
        order.line_items.reduce(
          (acc, item) => ({ ...acc, [item.id]: false }),
          {}
        )
      );
    }
  }, [order, changes, editing]);

  // Keep targetTotal in sync when the order total changes (e.g. after Set Total succeeds)
  useEffect(() => {
    if (displayTotalValue && !showSetTotalModal) {
      setTargetTotal(displayTotalValue);
    }
  }, [displayTotalValue, showSetTotalModal]);

  // useEffect(() => {
  //   console.log({ changes, order });
  // }, [changes, order]);

  // todo:
  // - Add save button and remove onBlur

  // Show loading indicator if order and shop are not loaded
  if (!order || !selectedShop) {
    return <LoadingPage />;
  }

  let items = order
    ? order.line_items.sort((a, b) => (a.title < b.title ? -1 : 1))
    : [];
  if (!user.isVendor) {
    items = items.filter(
      (item) =>
        item.fulfillable_quantity > 0 &&
        item.title.indexOf("Refundable") === -1 &&
        !item.title.toLowerCase().includes("picking & handling fee")
    );
  }

  const type = orderShippingMethod(order);
  const isDelivery = type === "delivery";
  const isPickupOrder = type === "pickup";
  const navigateToOrder = (orderId) => {
    navigateToShopify(`admin/orders/${orderId}`);
  };

  const handleChangeFulfillmentLocation = (locationId) => {
    setChangingLocation(true);
    changeFulfillmentLocation(order.id, locationId)
      .then(() => {
        // Success - close the modal
        setChangeLocationModalOpen(false);
      })
      .catch(() => {
        // Error - keep modal open so user can try again
        // Error message already shown via toast in admin context
      })
      .finally(() => {
        setChangingLocation(false);
      });
  };

  const hasFulfillmentInfo =
    order.fulfillment_order ||
    (order.fulfillments && order.fulfillments.length > 0);

  // Get all fulfillment locations with their line items
  const getFulfillmentLocations = () => {
    const locations = [];

    // Helper to check if an item should be excluded from counts (fees, holds, etc.)
    const isExcludedItem = (name) => {
      if (!name) return false;
      const lowerName = name.toLowerCase();
      return (
        lowerName.includes("refundable") ||
        lowerName.includes("picking & handling fee")
      );
    };

    // Check if order has fulfillment_orders array (unfulfilled/partially fulfilled orders)
    if (order.fulfillment_orders && Array.isArray(order.fulfillment_orders)) {
      order.fulfillment_orders.forEach((fo) => {
        if (fo.assigned_location) {
          // Extract line item IDs - handle both new structure (line_item.id) and legacy (li.id)
          // Filter out refundable holds and picking fees
          const filteredLineItems =
            fo.line_items?.filter((li) => {
              const itemName = li.line_item?.name || "";
              return !isExcludedItem(itemName);
            }) || [];

          // Helper to extract numeric ID from GraphQL ID (e.g., "gid://shopify/LineItem/123" -> "123")
          const extractNumericId = (gid) => {
            if (!gid) return gid;
            const match = String(gid).match(/\/(\d+)$/);
            return match ? match[1] : gid;
          };

          const lineItemIds = filteredLineItems.map((li) => {
            // New structure: line_item contains the actual line item data
            if (li.line_item?.id) {
              return extractNumericId(li.line_item.id);
            }
            // Legacy structure: li.id is also a GraphQL ID
            return extractNumericId(li.id);
          });

          locations.push({
            name:
              fo.assigned_location.location?.name || fo.assigned_location.name,
            address: fo.assigned_location.address1,
            lineItemIds: lineItemIds,
            lineItems: filteredLineItems, // Include full line item data for detailed display
            status: fo.status,
          });
        }
      });
    }
    // Fallback to single fulfillment_order (legacy structure)
    else if (order.fulfillment_order?.assigned_location) {
      const location = order.fulfillment_order.assigned_location;
      locations.push({
        name: location.location?.name || location.name,
        address: location.address1,
        lineItemIds: [],
        status: order.fulfillment_order.status,
      });
    }
    // Check fulfilled orders
    else if (
      order.fulfillments &&
      order.fulfillments.length > 0 &&
      selectedShop?.locations
    ) {
      order.fulfillments.forEach((fulfillment) => {
        const location = selectedShop.locations.find(
          (l) => l.id === fulfillment.location_id
        );
        if (location) {
          // Filter out refundable holds and picking fees
          const filteredLineItems =
            fulfillment.line_items?.filter((li) => {
              const itemName = li.name || li.title || "";
              return !isExcludedItem(itemName);
            }) || [];

          locations.push({
            name: location.name,
            address: location.address1,
            lineItemIds: filteredLineItems.map((li) => li.id),
            status: "fulfilled",
          });
        }
      });
    }

    return locations;
  };

  const fulfillmentLocations = getFulfillmentLocations();

  const fulfillmentActions = [];

  const hasMultipleFulfillmentLocations = fulfillmentLocations.length > 1;

  if (!user.isVendor && hasFulfillmentInfo) {
    if (hasMultipleFulfillmentLocations) {
      fulfillmentActions.push({
        content: "Change Fulfillment Location",
        onAction: () => {
          setToast(
            "This order has multiple fulfillment locations. Please change each location separately from the Shopify admin."
          );
        },
        disabled: true,
      });
    } else {
      fulfillmentActions.push({
        content: "Change Fulfillment Location",
        onAction: () => setChangeLocationModalOpen(true),
      });
    }
  }

  const adminLinks =
    user && user.isAdmin && isDelivery
      ? [
          {
            content: "Log Service Object",
            onAction: () => console.log({ service: order.service }),
          },
        ]
      : [];

  const hasTag = (tag) =>
    (order.tags || "")
      .toLowerCase()
      .split(",")
      .map((t) => t.trim())
      .includes(tag);

  const isPicked = hasTag("picked");
  const isPickedUp = hasTag("picked-up");
  const isDeliveredToCustomer = hasTag("delivered-to-customer");

  const handleSave = () => {
    setSaving(true);
    // console.log({ changes })
    postData(`/api/shops/${selectedShopId}/orders/${order.id}/shop`, {
      changes: changes,
    })
      .then((res) => res.json())
      .then((res) => {
        updateOrder(res.order);
        setChanges({});
        setEditing({});
        setToast("Order updated!");
      })
      .catch((err) => console.error(err))
      .finally(() => setSaving(false));
  };

  const handleMarkPickedUp = () => {
    setMarkingPickedUp(true);
    markOrderPickedUp(order.id).finally(() => setMarkingPickedUp(false));
  };

  const handleMarkDelivered = () => {
    setMarkingDelivered(true);
    markOrderDelivered(order.id).finally(() => setMarkingDelivered(false));
  };

  const handleSetTotal = () => {
    setSettingTotal(true);
    setOrderTotal(order.id, targetTotal, totalNote)
      .then((updated) => setTargetTotal(updated.total_price))
      .finally(() => {
        setSettingTotal(false);
        setShowSetTotalModal(false);
        setTotalNote("");
        setConfirmTotalChange(false);
      });
  };

  const cancelEdit = (item) => {
    setEditing({ ...editing, [item.id]: false });
    resetChange(item);
  };

  const resetChange = (item) => {
    const temp = { ...changes };
    delete temp[item.id];
    setChanges(temp);
  };

  const startEdit = (item) => {
    setEditing({ ...editing, [item.id]: true });
  };

  const updateChange = (itemId, type, payload) => {
    const change = { type, payload };
    const existing =
      changes[itemId] && changes[itemId].find((x) => x.type === type);

    if (existing) {
      setChanges({
        ...changes,
        [itemId]: changes[itemId].map((x) => (x.type === type ? change : x)),
      });
    } else {
      setChanges({
        ...changes,
        [itemId]: [...(changes[itemId] || []), change],
      });
    }
  };

  const changePrice = (item, original_price, actual_price) => {
    if (original_price === actual_price) {
      resetChange(item);
    } else {
      updateChange(item.id, changeTypes.updatePrice, {
        original_price,
        actual_price,
      });
    }
  };

  const changeQuantity = (item, quantity) => {
    updateChange(item.id, changeTypes.updateQuantity, { quantity });
  };

  const substitute = (item) => {
    setOpenSearchModal(item);
  };

  const handleCloseSubstituteModal = (variant) => {
    setOpenSearchModal(false);
    if (variant) {
      const item = openSearchModal;
      updateChange(item.id, changeTypes.substituteVariant, { variant });
    }
  };

  const hasChanges = Object.keys(changes).length > 0;

  const getChangeValue = (itemId, type) => {
    const change =
      changes[itemId] && changes[itemId].find((x) => x.type === type);
    if (!change) {
      return null;
    }

    if (type === changeTypes.updatePrice) {
      return change.payload.actual_price;
    }
    if (type === changeTypes.updateQuantity) {
      return `${change.payload.quantity}`;
    }
    if (type === changeTypes.substituteVariant) {
      return change.payload.variant.title;
    }
  };

  const statusBadges = [
    isPicked ? (
      <Badge key="picked" status="success">
        Picked
      </Badge>
    ) : null,
    isPickedUp ? (
      <Badge key="picked-up" status="info">
        Picked Up
      </Badge>
    ) : null,
    isDeliveredToCustomer ? (
      <Badge key="delivered" status="success">
        Delivered to Customer
      </Badge>
    ) : null,
  ].filter(Boolean);

  const displayedStatuses =
    statusBadges.length > 0
      ? statusBadges
      : [
          <Badge key="pending" status="attention">
            Status Pending
          </Badge>,
        ];

  const secondaryActions = [
    { content: "View Order", onAction: () => navigateToOrder(order.id) },
    {
      content: "Packing Slip",
      onAction: () => setShowPackingSlipModal(true),
    },
    delivery && delivery.shipping_label
      ? {
          content: "Shipping Label",
          onAction: () =>
            navigateExternal(
              delivery.shipping_label.replace("http://", "https://")
            ),
        }
      : null,
    delivery && order?.service?.provider === "Trexity" && delivery.id
      ? {
          content: "View Label",
          onAction: async () => {
            setParcelLabelOpen(true);
            setParcelLabelLoading(true);
            setParcelLabelFilename(
              `${order?.name || order?.id} - Parcel Label.pdf`
            );

            if (parcelLabelUrl) URL.revokeObjectURL(parcelLabelUrl);
            setParcelLabelUrl(null);

            const blob = await fetchBlobFromLink(
              `/api/deliveries/${delivery.id}/label_pdf/0`,
              { headers: { Accept: "application/pdf" } }
            );
            if (blob) setParcelLabelUrl(URL.createObjectURL(blob));
            setParcelLabelLoading(false);
          },
        }
      : null,
    ...fulfillmentActions,
    isPickupOrder && isPicked && !isPickedUp
      ? {
          content: "Mark as Picked Up",
          onAction: handleMarkPickedUp,
          loading: markingPickedUp,
        }
      : null,
    isDelivery && isPicked && !isDeliveredToCustomer
      ? {
          content: "Mark as Delivered",
          onAction: handleMarkDelivered,
          loading: markingDelivered,
        }
      : null,
    order.contact_email
      ? {
          content: (
            <span style={{ display: "flex", alignItems: "center" }}>
              <EmailIcon style={{ marginRight: "1rem" }} />{" "}
              {order.contact_email}
            </span>
          ),
          onAction: () =>
            window.open(`mailto:${order.contact_email}`, "_blank"),
        }
      : null,
    order.shipping_address && order.shipping_address.phone
      ? {
          content: (
            <span style={{ display: "flex", alignItems: "center" }}>
              <PhoneIcon style={{ marginRight: "1rem" }} />
              {order.shipping_address.phone}
            </span>
          ),
          onAction: () =>
            window.open(`tel:${order.shipping_address.phone}`, "_blank"),
        }
      : null,
    ...adminLinks,
  ].filter((action) => action && action.content);

  return (
    <Page
      title={orderTitle(order)}
      breadcrumbs={[
        {
          content: "Orders",
          onAction: () => history.push({ pathname: "../orders" }),
        },
      ]}
      subtitle={`${order.id}`}
      secondaryActions={secondaryActions}
      separator
    >
      <Card sectioned>
        <Card.Header>
          <PrintOrders
            orders={[order]}
            deliveries={deliveries}
            title={order.name}
            trigger={() => (
              <Button size="slim" primary>
                Print Order
              </Button>
            )}
          />
        </Card.Header>
        <Card.Section>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "0.75rem",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              {displayedStatuses}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                flexWrap: "wrap",
              }}
            >
              {isPickupOrder && isPicked && !isPickedUp ? (
                <Button
                  size="slim"
                  primary
                  loading={markingPickedUp}
                  onClick={handleMarkPickedUp}
                >
                  Mark as Picked Up
                </Button>
              ) : null}
              {isDelivery && isPicked && !isDeliveredToCustomer ? (
                <Button
                  size="slim"
                  primary
                  loading={markingDelivered}
                  onClick={handleMarkDelivered}
                >
                  Mark as Delivered
                </Button>
              ) : null}
              <Button size="slim" onClick={() => setShowSetTotalModal(true)}>
                Set Total
              </Button>
            </div>
          </div>
        </Card.Section>
      </Card>

      {hasChanges ? (
        <ContextualSaveBar
          message="Unsaved changes"
          saveAction={{
            onAction: () => handleSave(),
            loading: saving,
            disabled: saving,
          }}
          discardAction={{
            onAction: () => {
              setChanges({});
              setEditing({});
            },
          }}
        />
      ) : (
        ""
      )}

      <ShippingLabelsModal
        open={selectedShippingLabel ? true : false}
        label={selectedShippingLabel}
        handleClose={() => setSelectedShippingLabel(false)}
      />

      <ParcelLabelModal
        open={parcelLabelOpen}
        onClose={() => {
          setParcelLabelOpen(false);
          setParcelLabelLoading(false);
          if (parcelLabelUrl) URL.revokeObjectURL(parcelLabelUrl);
          setParcelLabelUrl(null);
        }}
        pdfUrl={parcelLabelUrl}
        filename={parcelLabelFilename}
        loading={parcelLabelLoading}
      />

      <PackingSlipModal
        order={order}
        active={showPackingSlipModal}
        handleClose={() => setShowPackingSlipModal(false)}
      />

      <Modal
        open={showSetTotalModal}
        onClose={() => setShowSetTotalModal(false)}
        title="Set Order Total"
        primaryAction={{
          content: "Update Total",
          onAction: handleSetTotal,
          loading: settingTotal,
          disabled: !targetTotal || !confirmTotalChange,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => setShowSetTotalModal(false),
          },
        ]}
      >
        <Modal.Section>
          {console.log(`[OrderPage] Rendering SetTotalModal order=${order.name}`, {
            displayTotalValue,
            effective_total: order.effective_total,
            total_price: order.total_price,
            current_total_price: order.current_total_price,
            total_refunded: order.total_refunded,
            total_outstanding: order.total_outstanding
          })}
          <div style={{ marginBottom: "0.75rem" }}>
            <TextStyle variation="subdued">
              Current total: {order.currency || "USD"} {displayTotalValue}
            </TextStyle>
          </div>
          <div style={{ marginBottom: "0.75rem" }}>
            <TextStyle>
              New total: {order.currency || "USD"}{" "}
              {targetTotal || displayTotalValue}
            </TextStyle>
          </div>
          <div style={{ marginBottom: "0.75rem" }}>
            <TextStyle variation="strong">
              {(() => {
                const current = parseFloat(displayTotalValue || 0) || 0;
                const target = parseFloat(targetTotal || 0) || 0;
                const delta = target - current;
                if (delta > 0) {
                  return `This will attempt to collect ${order.currency ||
                    "USD"} ${delta.toFixed(2)}`;
                }
                if (delta < 0) {
                  return `This will refund ${order.currency ||
                    "USD"} ${Math.abs(delta).toFixed(2)}`;
                }
                return "No payment change will occur.";
              })()}
            </TextStyle>
          </div>
          {(() => {
            const current = parseFloat(displayTotalValue || 0) || 0;
            const target = parseFloat(targetTotal || 0) || 0;
            const delta = target - current;
            if (delta > 0) {
              return (
                <div style={{ marginBottom: "0.75rem", padding: "0.5rem", backgroundColor: "#FFF4E5", borderRadius: "4px", border: "1px solid #FFB020" }}>
                  <TextStyle variation="strong">
                    Note: If auto-collection fails (e.g. card on file cannot be charged for the additional {order.currency || "USD"} {delta.toFixed(2)}),
                    you will need to collect the remaining amount manually or send an invoice from the Shopify dashboard.
                  </TextStyle>
                </div>
              );
            }
            return null;
          })()}
          <TextField
            label="Total Amount"
            type="number"
            min="0"
            value={`${targetTotal || ""}`}
            onChange={(val) => setTargetTotal(val)}
            prefix={order.currency || "USD"}
            autoComplete="off"
          />
          <div style={{ marginTop: "1rem" }}>
            <TextField
              label="Adjustment Note (optional)"
              value={totalNote}
              onChange={(val) => setTotalNote(val)}
              multiline
              autoComplete="off"
              placeholder="Reason for price adjustment or refund"
            />
          </div>
          <div style={{ marginTop: "1rem" }}>
            <Checkbox
              label="I confirm the above payment action (collect or refund) is correct"
              checked={confirmTotalChange}
              onChange={(val) => setConfirmTotalChange(val)}
            />
          </div>
          <div style={{ marginTop: "0.75rem" }}>
            <TextStyle variation="subdued">
              To fully process the refund or settle any remaining balance and
              send an invoice, you must open this order in Shopify and complete
              the refund or payment collection there.
            </TextStyle>
          </div>
        </Modal.Section>
      </Modal>

      <SearchProductsModal
        active={openSearchModal}
        handleClose={handleCloseSubstituteModal}
        loadingProducts={loadingProducts}
      />

      {selectedShop?.locations && (
        <>
          <ChangeFulfillmentLocationModal
            open={changeLocationModalOpen}
            order={order}
            locations={selectedShop.locations}
            currentLocationId={
              order.fulfillment_order?.assigned_location?.location?.id ||
              order.fulfillments?.[0]?.location_id
            }
            onSave={handleChangeFulfillmentLocation}
            onClose={() => setChangeLocationModalOpen(false)}
            saving={changingLocation}
          />
        </>
      )}

      {saving ? "Saving... This may take a moment" : ""}
      {/* changes ? changes.filter(x => x.type === changeTypes.updatePrice).map(x => x.payload.actual_price - x.payload.original_price).reduce((a, b) => a + b, 0) : 'None' */}

      {userCanView ? (
        <Card>
          {fulfillmentLocations.length > 0 && (
            <Card.Section>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <TextContainer>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <TextStyle variation="strong">
                      {fulfillmentLocations.length === 1
                        ? "Fulfillment Location"
                        : `Fulfillment Locations (${fulfillmentLocations.length})`}
                    </TextStyle>
                    <Badge status={type === "pickup" ? "info" : "warning"}>
                      {type === "pickup" ? "Pickup" : "Delivery"}
                    </Badge>
                  </div>
                  {fulfillmentLocations.map((location, index) => (
                    <div
                      key={index}
                      style={{
                        marginTop: index > 0 ? "0.75rem" : "0",
                        paddingTop: index > 0 ? "0.75rem" : "0",
                        borderTop: index > 0 ? "1px solid #e1e3e5" : "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          flexWrap: "wrap",
                        }}
                      >
                        <Badge status="info">{location.name}</Badge>
                        {location.status && (
                          <Badge
                            status={
                              location.status === "fulfilled"
                                ? "success"
                                : "attention"
                            }
                          >
                            {location.status}
                          </Badge>
                        )}
                        {location.address && (
                          <TextStyle variation="subdued">
                            • {location.address}
                          </TextStyle>
                        )}
                        {location.lineItemIds.length > 0 && (
                          <TextStyle variation="subdued">
                            • {location.lineItemIds.length} item
                            {location.lineItemIds.length !== 1 ? "s" : ""}
                          </TextStyle>
                        )}
                      </div>
                    </div>
                  ))}
                </TextContainer>
              </div>
            </Card.Section>
          )}
          <ResourceList
            resourceName={{ singular: "product", plural: "products" }}
            items={items}
            loading={saving}
            renderItem={(item) => {
              const {
                id,
                variant_id,
                product,
                name,
                vendor,
                price,
                total_discount,
                quantity,
                fulfillable_quantity,
              } = item;
              const qty = user.isVendor ? quantity : fulfillable_quantity;
              const totalPrice = `${(+price * +qty - +total_discount).toFixed(
                2
              )}`;
              const subVariant = getChangeValue(
                id,
                changeTypes.substituteVariant
              );
              const substitution = subVariant ? (
                <TextStyle variation="subdued">
                  Substitute with "{subVariant}"
                </TextStyle>
              ) : null;
              const thumbnail = (
                <Thumbnail
                  source={
                    product && product.image && product.image.src
                      ? product.image.src
                      : null
                  }
                />
              );
              return (
                <ResourceItem
                  id={id}
                  media={thumbnail}
                  onClick={(itemId) => {
                    if (userCanEdit && !editing[id]) {
                      startEdit(item, totalPrice);
                    }
                  }}
                  accessibilityLabel={`View details for ${name}`}
                >
                  <ListItem>
                    <div>
                      <TextStyle variation="subdued">{vendor}</TextStyle>
                      <br />
                      <TextStyle variation="strong">{name}</TextStyle>
                      {editing[id] ? "" : variant_id ? ` x ${qty}` : ""}
                      <br />
                      {substitution}
                      <div>Price: ${totalPrice}</div>
                    </div>
                    {editing[id] ? (
                      <div style={{ display: "flex", marginLeft: "auto" }}>
                        <div style={{ maxWidth: "9rem", marginRight: "1rem" }}>
                          <TextField
                            type="quantity"
                            min="0"
                            value={
                              getChangeValue(id, changeTypes.updateQuantity) ||
                              `${qty}`
                            }
                            onChange={(val) =>
                              changeQuantity(item, parseInt(val || 0))
                            }
                          />
                        </div>

                        <div style={{ maxWidth: "11rem", marginRight: "1rem" }}>
                          <TextField
                            prefix="$"
                            type="price"
                            min="0"
                            value={
                              getChangeValue(id, changeTypes.updatePrice) ||
                              totalPrice
                            }
                            onChange={(val) =>
                              changePrice(item, totalPrice, val || "0")
                            }
                          />
                        </div>

                        <IconButton onClick={() => substitute(item)}>
                          <CompareArrowsIcon />
                        </IconButton>
                        <IconButton onClick={() => cancelEdit(item)}>
                          <CloseIcon />
                        </IconButton>
                      </div>
                    ) : (
                      ""
                    )}
                  </ListItem>
                </ResourceItem>
              );
            }}
          />
        </Card>
      ) : (
        ""
      )}
    </Page>
  );
}
